import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sprout, Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { contactInfo } from '../data';

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleNavClick = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 80;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <footer className="bg-emerald-950 text-white pt-16 pb-8 relative overflow-hidden">
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center pointer-events-none opacity-40"
        style={{ 
          backgroundImage: "url('https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/4817d1f4-a80f-4dbe-aca5-664c58aee383.jpg')" 
        }}
      />
      
      {/* Dark overlay to ensure text readability */}
      <div className="absolute inset-0 bg-emerald-950/90 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          
          {/* Brand Column */}
          <div className="md:col-span-4">
            <div 
              onClick={() => {
                if (location.pathname !== '/') {
                  navigate('/');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  scrollToTop();
                }
              }} 
              className="flex flex-col items-start cursor-pointer group mb-6"
            >
              <div className="transition-transform duration-300 group-hover:scale-105">
                <img 
                  src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/594eaf4a-ed99-49a0-bf64-94af839ff8a9.png" 
                  alt="Anlægsgartneren fra Thy" 
                  className="h-24 sm:h-32 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-xs text-white/60">
              CVR-nummer: {contactInfo.cvr} · Fuldt forsikret
            </p>
          </div>

          {/* Navigation Links */}
          <div className="md:col-span-2">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Hurtige links</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <button 
                  onClick={() => handleNavClick('forside')} 
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Forside
                </button>
              </li>
              <li>
                <Link 
                  to="/galleri" 
                  onClick={scrollToTop}
                  className="text-white/80 hover:text-white transition-colors cursor-pointer block"
                >
                  Galleri
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => handleNavClick('om-mig')} 
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Om mig
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavClick('kontakt')} 
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-left"
                >
                  Kontakt os
                </button>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div className="md:col-span-3">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Åbningstider</h4>
            <ul className="space-y-3 text-sm">
              {contactInfo.hours.map((hour, index) => (
                <li key={index} className="flex justify-between gap-2 border-b border-white/20 pb-1.5">
                  <span className="text-white/90">{hour.days}</span>
                  <span className="text-white font-semibold">{hour.time}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="md:col-span-3">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Kontaktoplysninger</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-white flex-shrink-0" />
                <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="text-white/80 hover:text-white transition-colors font-semibold">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-white flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-white/80 hover:text-white transition-colors font-semibold">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-white flex-shrink-0" />
                <p className="text-white font-semibold">{contactInfo.address}</p>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/60 text-center sm:text-left">
            © {new Date().getFullYear()} Anlægsgartneren fra Thy. Alle rettigheder forbeholdes.
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/80 hover:text-white transition-colors bg-transparent cursor-pointer"
          >
            Til top
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>

      </div>
    </footer>
  );
}
